import {
  ThermometerSnowflake,
  Wind,
  Droplet,
  Droplets,
  Snowflake,
  Volume2,
  AlertTriangle,
  Gauge,
  ShieldCheck,
  Phone,
} from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/motion";
import { CTAButton } from "@/components/CTAButton";
import {
  Section,
  SectionHeading,
  Breadcrumb,
  ProcessSteps,
  InfoGrid,
  CTABanner,
} from "@/components/Sections";
import { BUSINESS } from "@/lib/site";

const PROBLEMS = [
  { icon: "ThermometerSnowflake", title: "AC Not Cooling", text: "Inspection and diagnostics for poor cooling performance." },
  { icon: "Wind", title: "Weak Airflow", text: "Checks for restricted airflow and internal causes." },
  { icon: "Droplet", title: "Water Leaks", text: "Inspection of condensate drainage and related causes." },
  { icon: "Droplets", title: "Blocked Drainage", text: "Drain troubleshooting and practical drainage repair." },
  { icon: "Snowflake", title: "AC Freezing", text: "Inspection of airflow, refrigerant and operating conditions." },
  { icon: "Volume2", title: "Unusual Noises", text: "Checks to identify abnormal sounds and possible causes." },
  { icon: "AlertTriangle", title: "Error Codes", text: "Inspection and troubleshooting of displayed system faults." },
  { icon: "Gauge", title: "Gas / Refrigerant Issues", text: "Checks for refrigerant-related cooling problems and system condition." },
];
const ICONS = { ThermometerSnowflake, Wind, Droplet, Droplets, Snowflake, Volume2, AlertTriangle, Gauge };

const STEPS = [
  { title: "Inspection", text: "Assess the symptoms and overall system condition." },
  { title: "Diagnosis", text: "Carry out professional checks to identify the likely cause of the fault." },
  { title: "Repair Recommendation", text: "Explain the fault and the appropriate practical repair." },
  { title: "Repair & Testing", text: "Carry out the agreed repair where possible and test the system afterwards." },
];

export default function Repair() {
  return (
    <>
      <SEO
        title="AC Repair & Diagnostics Paphos | NorthWork Cyprus"
        description="Professional AC repair and diagnostics in Paphos. Systematic inspection for cooling problems, leaks, drainage faults, weak airflow, freezing, noises and error codes. NorthWork Cyprus."
        path="/ac-repair-paphos"
        schema={{ "@type": "Service", serviceType: "AC Repair & Diagnostics", areaServed: "Paphos" }}
      />
      <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "AC Repair & Diagnostics" }]} />

      {/* Hero */}
      <section data-testid="page-hero" className="relative isolate overflow-hidden border-b border-border">
        {/* Full-width background image */}
        <img
          src="/images/repair-hero-bg.webp"
          alt="NorthWork Cyprus technician diagnosing an open air-conditioning unit's circuit board with a multimeter in Paphos"
          loading="eager"
          className="absolute inset-0 -z-20 h-full w-full object-cover object-[82%_58%] md:object-right"
        />
        {/* Dark overlay: stronger behind the left/text on mobile, repair equipment kept brighter on the right */}
        <div
          className="absolute inset-0 -z-10 bg-gradient-to-r from-[#0b0e14]/97 via-[#0b0e14]/72 to-[#0b0e14]/16 md:from-[#0b0e14]/95 md:via-[#0b0e14]/80 md:to-[#0b0e14]/40"
          aria-hidden="true"
        />
        <div className="container-x grid items-center gap-10 py-7 md:py-12 lg:grid-cols-12 lg:gap-14 lg:py-20">
          <div className="lg:col-span-7">
            <Reveal variant="up">
              <h1 className="font-heading text-[2.55rem] font-medium leading-[1.02] tracking-tight text-white md:text-5xl md:leading-[1.04] lg:text-[4rem]">
                AC Repair &amp; Diagnostics<br /> in Paphos
              </h1>
            </Reveal>
            <Reveal delay={0.1} variant="up" className="mt-4 max-w-xl md:mt-6">
              <p className="text-base leading-relaxed text-white/85 md:hidden">
                Professional AC inspection, diagnostics and repair for cooling problems, leaks,
                drainage faults, freezing and other operating issues.
              </p>
              <p className="hidden text-base leading-relaxed text-white/85 md:block lg:text-lg">
                Professional AC inspection, diagnostics and practical repair for cooling problems,
                leaks, drainage faults, freezing, weak airflow, unusual noises and other operating
                issues.
              </p>
            </Reveal>
            <Reveal delay={0.15} variant="up" className="mt-6 flex flex-col gap-3 sm:flex-row md:mt-8">
              <CTAButton to="/contact" testid="hero-primary-cta">
                Book an Inspection
              </CTAButton>
              <CTAButton href={`tel:${BUSINESS.phoneTel}`} variant="secondary" icon={Phone} testid="hero-call-cta" className="hidden md:inline-flex">
                {BUSINESS.phoneDisplay}
              </CTAButton>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <Section className="py-16 lg:py-20">
        <div className="container-x grid gap-8 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeading title="Find the Cause of the Problem" />
          </div>
          <Reveal delay={0.1} className="space-y-5 text-base leading-relaxed text-charcoal-muted lg:col-span-7 lg:text-lg">
            <p>
              When an air conditioner is not operating correctly, the first step is identifying the
              cause of the problem.
            </p>
            <p>
              NorthWork Cyprus provides professional AC inspection and diagnostics to identify the
              cause of faults and determine the appropriate repair.
            </p>
          </Reveal>
        </div>
      </Section>

      {/* Common problems */}
      <Section className="border-t border-border bg-surface py-16 lg:py-20">
        <div className="container-x">
          <SectionHeading title="Common AC Problems" />
          <div className="mt-10">
            <InfoGrid items={PROBLEMS} columns={4} iconMap={ICONS} compact testid="problems-grid" />
          </div>
        </div>
      </Section>

      {/* Practical repair */}
      <Section className="py-16 lg:py-20">
        <div className="container-x">
          <Reveal>
            <div className="grid gap-8 border border-border p-6 lg:grid-cols-12 lg:items-center lg:gap-14 lg:p-10">
              <div className="lg:col-span-2">
                <span className="grid h-14 w-14 place-items-center border border-border text-brand">
                  <ShieldCheck className="h-7 w-7" />
                </span>
              </div>
              <div className="lg:col-span-10">
                <h2 className="font-heading text-2xl font-medium tracking-tight text-charcoal sm:text-3xl">
                  Practical Repair. Clear Advice.
                </h2>
                <p className="mt-4 max-w-3xl text-base leading-relaxed text-charcoal-muted lg:text-lg">
                  NorthWork Cyprus provides professional AC inspection and diagnostics to identify
                  the cause of faults and determine the appropriate repair.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* Process */}
      <Section className="border-t border-border bg-surface py-16 lg:py-20">
        <div className="container-x">
          <SectionHeading title="Repair Process" />
          <div className="mt-10">
            <ProcessSteps steps={STEPS} compact testid="repair-process" />
          </div>
        </div>
      </Section>

      <CTABanner
        title="Is Your AC Not Working Properly?"
        text="Arrange professional AC inspection and diagnostics with NorthWork Cyprus in Paphos."
        primaryLabel="Book Inspection"
        primaryTo="/contact"
        image="/images/repair-cta.webp"
        imageClass="object-[76%_center] lg:object-[60%_center]"
        paddingClass="py-16 lg:py-24"
        overlayStyle="linear-gradient(to right, rgba(6,13,26,0.72) 0%, rgba(6,13,26,0.52) 30%, rgba(6,13,26,0.28) 55%, rgba(6,13,26,0.08) 78%, rgba(6,13,26,0) 100%)"
      />
    </>
  );
}
